// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

export function TemplateDownload(props: TemplateDownloadProps) {
  const handleDownloadTemplate = useCallback(() => {
    const headers = ['Shipment number', 'Shipping cost', 'Currency (Ex USD)'];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `shipment_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for shipment data
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>
              <Typography variant="body2">Shipment number - text</Typography>
            </li>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Available fields
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>
              <Typography variant="body2">
                Shipping cost - number or blank (ex: 123.45)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Currency - ISO code (ex: USD); defaults to your organization
                currency if blank
              </Typography>
            </li>
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            <li>Shipment number must match an existing shipment</li>
            <li>
              Include the shipping cost column only when you want to update or
              clear shipping cost
            </li>
            <li>
              A blank shipping cost cell clears the existing shipping cost
              record from that shipment
            </li>
            <li>
              Delete the shipping cost column to leave existing shipping costs
              unchanged
            </li>
            <li>
              Shipping cost should be a positive currency value with no more
              than two decimal places when provided
            </li>
            <li>
              This uploader updates existing shipment data; it does not create
              shipments
            </li>
          </Box>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
